import type { Pinia } from 'pinia';

// This interface is intentionally open for module augmentation.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AppModuleServiceMap {
}

export type AppModuleComponentLoader = () => Promise<unknown>;
export type AppModuleComponentMap = Record<string, AppModuleComponentLoader>;

export interface AppModuleComponentsDefinition {
    global?: AppModuleComponentMap;
    local?: AppModuleComponentMap;
}

export interface AppModuleUseDefinition {
    components?: AppModuleComponentsDefinition;
    router?: unknown;
    store?: unknown;
    plugins?: unknown;
    manager?: AppModuleDefinition;

    [key: string]: unknown;
}

export interface AppModuleResolvedUse {
    components?: {
        global: string[];
        local: string[];
    };
    router?: unknown;
    store?: unknown;
    plugins?: unknown;
    manager?: RegisteredAppModule | undefined;

    [key: string]: unknown;
}

export type AppModuleService<Name extends string = string> = Name extends keyof AppModuleServiceMap
    ? AppModuleServiceMap[Name]
    : unknown;

export interface AppModuleContext {
    pinia: Pinia;
    router: unknown;
    store: unknown;
}

export type AppModuleServiceFactory<Name extends string = string> = (
    context: AppModuleContext,
) => AppModuleService<Name>;

export type AppModuleServiceDefinition<Name extends string = string> =
    | AppModuleService<Name>
    | AppModuleServiceFactory<Name>;

export interface AppModuleDefinition<Name extends string = string> {
    use?: AppModuleUseDefinition;
    service?: AppModuleServiceDefinition<Name>;

    [key: string]: unknown;
}

export interface RegisteredAppModule<Name extends string = string> extends Omit<AppModuleDefinition<Name>, 'use' | 'service'> {
    name: Name;
    use: AppModuleResolvedUse;
    service?: AppModuleService<Name>;
}

export interface ImportedAppModule<Name extends string = string> {
    NAME: Name;
    default: AppModuleDefinition<Name>;
}

const appModules = new Map<string, RegisteredAppModule>();

export function hasAppModule(name: string) {
    return appModules.has(name);
}

export function getAppModule<Name extends string>(name: Name) {
    return appModules.get(name) as RegisteredAppModule<Name> | undefined;
}

export function getAppModuleService<Name extends string>(name: Name) {
    return getAppModule(name)?.service as AppModuleService<Name> | undefined;
}

export function resolveAppModuleService<Name extends string>(
    service: AppModuleServiceDefinition<Name> | undefined,
    context: AppModuleContext,
): AppModuleService<Name> | undefined {
    if (typeof service === 'function') {
        return (service as AppModuleServiceFactory<Name>)(context);
    }

    return service as AppModuleService<Name> | undefined;
}

export function listAppModules() {
    return [...appModules.values()];
}

export function listAppModuleNames() {
    return listAppModules().map(({ name }) => name);
}

export function forEachAppModule(
    callback: (module: RegisteredAppModule, name: string) => void,
) {
    appModules.forEach((module, name) => {
        callback(module, name);
    });
}

export function registerAppModule(module: RegisteredAppModule) {
    const registeredModule = getAppModule(module.name);

    if (registeredModule) {
        return registeredModule;
    }

    appModules.set(module.name, module);

    return module;
}
